import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth, useCan } from './useAuth'
import type { Permission } from './permissions'
import './guards.css'

/**
 * Zugangsschranken der Oberflaeche.
 *
 * Erinnerung an alle, die hier etwas ergaenzen: das ist BEQUEMLICHKEIT, keine
 * Sicherheit. Die Grenze zieht RLS in Postgres. Wer diese Komponenten im
 * Browser aushebelt, bekommt eine leere Seite und eine 403 vom Server — nicht
 * die Daten.
 */

function FullScreen({
  title,
  children,
  tone = 'neutral',
}: {
  title: string
  children?: ReactNode
  tone?: 'neutral' | 'danger'
}) {
  return (
    <div className="guard-screen" data-tone={tone}>
      <div className="guard-screen__card">
        <p className="eyebrow">NEURA Performance</p>
        <h1 className="guard-screen__title">{title}</h1>
        {children}
      </div>
    </div>
  )
}

/** Ladeanzeige, solange die Sitzung noch unbekannt ist. */
export function AuthLoading({ label = 'Sitzung wird geprüft' }: { label?: string }) {
  return (
    <div className="guard-screen" role="status" aria-live="polite">
      <div className="guard-screen__pulse" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <p className="guard-screen__label">{label}</p>
    </div>
  )
}

/**
 * Alles hinter dieser Schranke setzt eine gueltige Sitzung voraus.
 *
 * Merkt sich den angesteuerten Pfad und springt nach dem Login dorthin zurueck —
 * sonst landet jeder Deep Link nach dem Anmelden stumpf auf der Startseite.
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const { session, profile, globalRole, loading, error } = useAuth()
  const location = useLocation()

  if (session === undefined) {
    return <AuthLoading />
  }

  if (session === null) {
    const returnTo = location.pathname + location.search
    return (
      <Navigate
        to={`/login${returnTo !== '/' ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`}
        replace
      />
    )
  }

  if (loading) {
    return <AuthLoading label="Berechtigungen werden geladen" />
  }

  if (error) {
    return (
      <FullScreen title="Kontext nicht ladbar" tone="danger">
        <p className="guard-screen__text">
          Die Berechtigungen konnten nicht abgerufen werden. Meist steckt eine
          fehlende Konfiguration dahinter, seltener ein Netzproblem.
        </p>
        <p className="guard-screen__detail numeric">{error}</p>
      </FullScreen>
    )
  }

  // Konto gesperrt — das Profil ist weiterhin lesbar, damit diese Meldung
  // ueberhaupt erscheinen kann. Alle anderen Tabellen liefern nichts mehr.
  if (profile?.status === 'suspended') {
    return (
      <FullScreen title="Konto gesperrt" tone="danger">
        <p className="guard-screen__text">
          Dieses Konto ist derzeit gesperrt. Wenden Sie sich an die
          Administration des Performance-Intranets.
        </p>
      </FullScreen>
    )
  }

  // Angemeldet, aber ohne Rolle: passiert, wenn jemand ohne Einladung
  // hereinkommt. Bewusst fail-closed statt einer stillen Standardrolle.
  if (!globalRole) {
    return (
      <FullScreen title="Noch nicht freigeschaltet">
        <p className="guard-screen__text">
          Die Anmeldung hat funktioniert, dem Konto ist aber noch keine Rolle
          zugewiesen. Eine Administratorin oder ein Administrator muss das
          einmalig freigeben.
        </p>
        <p className="guard-screen__detail numeric">{profile?.email}</p>
      </FullScreen>
    )
  }

  return <>{children}</>
}

/**
 * Route hinter einem einzelnen Recht. Fehlt es, gibt es eine ehrliche
 * Meldung statt einer leeren Seite.
 */
export function RequirePermission({
  permission,
  spaceId,
  children,
}: {
  permission: Permission
  spaceId?: string
  children: ReactNode
}) {
  const allowed = useCan(permission, spaceId)

  if (!allowed) {
    return (
      <FullScreen title="Kein Zugriff">
        <p className="guard-screen__text">
          Für diesen Bereich fehlt Ihnen das nötige Recht.
        </p>
        <p className="guard-screen__detail numeric">{permission}</p>
      </FullScreen>
    )
  }

  return <>{children}</>
}

/**
 * Blendet einzelne Bedienelemente aus.
 *
 *   <Can permission="document:publish" spaceId={space.id}>
 *     <Button>Freigeben</Button>
 *   </Can>
 */
export function Can({
  permission,
  spaceId,
  fallback = null,
  children,
}: {
  permission: Permission
  spaceId?: string
  fallback?: ReactNode
  children: ReactNode
}) {
  return useCan(permission, spaceId) ? <>{children}</> : <>{fallback}</>
}

/** Kehrt die Bedingung um — fuer Hinweise an alle, die etwas NICHT dürfen. */
export function Cannot({
  permission,
  spaceId,
  children,
}: {
  permission: Permission
  spaceId?: string
  children: ReactNode
}) {
  return useCan(permission, spaceId) ? null : <>{children}</>
}
